/**
 * Messages — Figma frame 175:35 (3-column layout).
 *
 *   Left     : threads list (search + unread total + per-thread preview)
 *   Middle   : active thread — student header, day divider, message bubbles,
 *              two-row composer (Write/Preview tabs + textarea + Send)
 *   Right    : student context sidebar (avatar + risk chip + metric stack +
 *              active flags + quick actions)
 *
 * The full ConversationThread component (with all its scroll/WS/optimistic
 * behaviour) still exists for other surfaces, but this route re-implements a
 * leaner variant inline so we can shape it to the Figma exactly. Backend
 * contract is preserved: same hooks (useConversations / useMessages /
 * useSendMessage / useMarkAsRead / useMessagesWebSocket).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { format, formatDistanceToNowStrict } from 'date-fns';
import {
  useConversation,
  useConversations,
  useMarkAsRead,
  useMessages,
  useMessagesWebSocket,
  useSendMessage,
  useStartConversation,
  type OptimisticMessage,
} from '../hooks/useMessages';
import { useStudentDetail } from '../hooks';
import { useAtRisk } from '../hooks/useAtRisk';
import { useToast } from '../components/Toast';
import type { Message } from '../services/messagesApi';
import styles from './Messages.module.css';

function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  if (parts.length === 0) return 'ST';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function relTime(iso: string): string {
  try {
    return formatDistanceToNowStrict(new Date(iso))
      .replace('minutes', 'm')
      .replace('minute', 'm')
      .replace('hours', 'h')
      .replace('hour', 'h')
      .replace('days', 'd')
      .replace('day', 'd')
      .replace('seconds', 's')
      .replace('second', 's')
      .replace(/\s/g, '');
  } catch {
    return iso;
  }
}

function bubbleTime(iso: string): string {
  try {
    return format(new Date(iso), 'HH:mm');
  } catch {
    return iso;
  }
}

const Messages: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationIdParam = searchParams.get('conversationId');
  const studentIdParam = searchParams.get('studentId');

  const [search, setSearch] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [composerTab, setComposerTab] = useState<'write' | 'preview'>('write');
  const [draft, setDraft] = useState('');
  const composerRef = useRef<HTMLTextAreaElement>(null);

  useMessagesWebSocket();

  const { data: conversations, isLoading, isError } = useConversations({
    unreadOnly: unreadOnly || undefined,
  });
  const startConversation = useStartConversation();

  // ?studentId=... bootstrap
  useEffect(() => {
    if (!studentIdParam) return;
    const existing = conversations?.find((c) => c.studentId === studentIdParam);
    if (existing) {
      setSearchParams({ conversationId: existing.id }, { replace: true });
      return;
    }
    startConversation.mutate(studentIdParam, {
      onSuccess: (convo) => {
        setSearchParams({ conversationId: convo.id }, { replace: true });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentIdParam, conversations]);

  const filteredConversations = useMemo(() => {
    if (!conversations) return [];
    const term = search.trim().toLowerCase();
    let list = conversations;
    if (term) {
      list = list.filter(
        (c) =>
          c.studentName.toLowerCase().includes(term) ||
          c.studentEmail.toLowerCase().includes(term) ||
          c.lastMessagePreview.toLowerCase().includes(term),
      );
    }
    return [...list].sort(
      (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime(),
    );
  }, [conversations, search]);

  const unreadTotal = useMemo(
    () => (conversations ?? []).reduce((sum, c) => sum + c.unreadCount, 0),
    [conversations],
  );

  const selectedConversation = filteredConversations.find(
    (c) => c.id === conversationIdParam,
  ) ?? (conversations ?? []).find((c) => c.id === conversationIdParam) ?? null;

  const { data: conversation } = useConversation(conversationIdParam ?? undefined);
  const activeConversation = conversation ?? selectedConversation;

  const { data: messagesPages } = useMessages(conversationIdParam ?? undefined);
  const sendMessage = useSendMessage();
  const markAsRead = useMarkAsRead();
  const toast = useToast();

  // Flatten pages (newest first per page) into an oldest-first array for display.
  const orderedMessages = useMemo<OptimisticMessage[]>(() => {
    if (!messagesPages) return [];
    const flat = messagesPages.pages.flat();
    return [...flat].sort(
      (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime(),
    );
  }, [messagesPages]);

  // Mark unread student messages as read once the conversation opens.
  useEffect(() => {
    if (!activeConversation || !orderedMessages.length) return;
    const unread = orderedMessages
      .filter((m) => m.senderRole === 'student' && !m.readAt)
      .map((m) => m.id);
    if (unread.length > 0) {
      markAsRead.mutate({ conversationId: activeConversation.id, messageIds: unread });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversation?.id, orderedMessages.length]);

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [orderedMessages.length, activeConversation?.id]);

  const selectConversation = (id: string) => {
    setSearchParams({ conversationId: id });
  };

  const handleSend = () => {
    const body = draft.trim();
    if (!body || !activeConversation) return;
    // The optimistic bubble is marked `failed` by the mutation on error; also
    // surface a toast so a send failure is never silently swallowed.
    sendMessage.mutate(
      { conversationId: activeConversation.id, body },
      {
        onError: () =>
          toast.showError('Message failed to send — tap Retry on the message to try again.'),
      },
    );
    setDraft('');
    composerRef.current?.focus();
  };

  // Retry a previously-failed optimistic message (reuses its clientId so the
  // hook replaces the failed copy in place rather than duplicating it).
  const handleRetry = (failed: OptimisticMessage) => {
    if (!activeConversation || !failed.clientId) return;
    sendMessage.mutate(
      { conversationId: activeConversation.id, body: failed.body, clientId: failed.clientId },
      {
        onError: () => toast.showError('Still couldn’t send — check your connection.'),
      },
    );
  };

  const handleComposerKey: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if ((e.key === 'Enter' && (e.metaKey || e.ctrlKey)) || (e.key === 'Enter' && !e.shiftKey && !composerTab.includes('preview'))) {
      // Enter → send; Shift+Enter → newline; ⌘/Ctrl+Enter also sends.
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    }
  };

  // ---------- Context sidebar data ----------
  const activeStudentId = activeConversation?.studentId;
  const { data: studentDetail } = useStudentDetail(activeStudentId ?? '');
  const { data: atRiskList } = useAtRisk();
  const atRiskRecord = atRiskList?.find((s) => s.studentId === activeStudentId);
  const isFlagged = !!atRiskRecord;

  return (
    <div className={styles.page}>
      {/* ---------- Threads list ---------- */}
      <aside className={styles.listPane} aria-label="Conversations">
        <header className={styles.listHeader}>
          <h1 className={styles.listTitle}>Messages</h1>
          {unreadTotal > 0 && (
            <span className={styles.unreadTotal} aria-label={`${unreadTotal} unread`}>
              {unreadTotal}
            </span>
          )}
        </header>

        <div className={styles.listSearch}>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search conversations…"
            className={styles.searchInput}
            aria-label="Search conversations"
          />
        </div>

        <div className={styles.unreadToggleRow}>
          <label className={styles.unreadToggle}>
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
            />
            Unread only
          </label>
        </div>

        {isLoading ? (
          <div className={styles.listEmpty}>Loading conversations…</div>
        ) : isError ? (
          <div className={styles.listEmpty}>Couldn&apos;t load conversations.</div>
        ) : filteredConversations.length === 0 ? (
          <div className={styles.listEmpty}>
            {search ? 'No conversations match that search.' : 'No conversations yet.'}
          </div>
        ) : (
          <ul className={styles.list}>
            {filteredConversations.map((c) => {
              const active = c.id === conversationIdParam;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => selectConversation(c.id)}
                    className={`${styles.listItem} ${active ? styles.listItemActive : ''}`}
                    aria-current={active ? 'true' : undefined}
                  >
                    <div className={styles.listRow}>
                      <span className={styles.avatar} aria-hidden="true">
                        {initialsFrom(c.studentName)}
                      </span>
                      <div className={styles.listBody}>
                        <div className={styles.listItemHeader}>
                          <span className={styles.listItemName}>{c.studentName}</span>
                          <span className={styles.listItemTime}>{relTime(c.lastMessageAt)}</span>
                        </div>
                        <div className={styles.listItemPreviewRow}>
                          <span
                            className={`${styles.listItemPreview} ${c.unreadCount > 0 ? styles.listItemPreviewUnread : ''}`}
                          >
                            {c.lastMessagePreview}
                          </span>
                          {c.unreadCount > 0 && (
                            <span className={styles.unreadBadge}>{c.unreadCount}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </aside>

      {/* ---------- Active thread ---------- */}
      <main className={styles.threadPane} aria-label="Active conversation">
        {!activeConversation ? (
          <div className={styles.emptyThread}>
            Select a conversation to open the thread.
          </div>
        ) : (
          <>
            <header className={styles.threadHeader}>
              <span className={styles.avatar} aria-hidden="true">
                {initialsFrom(activeConversation.studentName)}
              </span>
              <div className={styles.threadHeaderCopy}>
                <h2 className={styles.threadName}>{activeConversation.studentName}</h2>
                <span className={styles.threadSubtitle}>
                  AI Engineering  ·  Active{' '}
                  {formatDistanceToNowStrict(new Date(activeConversation.lastMessageAt), {
                    addSuffix: true,
                  })}
                </span>
              </div>
              <Link
                to={`/students/${activeConversation.studentId}`}
                className={styles.viewProfile}
              >
                View profile
              </Link>
            </header>

            <div className={styles.transcript} ref={scrollRef} role="log" aria-live="polite">
              <div className={styles.dayDivider}>Today</div>
              {orderedMessages.map((m: OptimisticMessage) => {
                if (m.senderRole === 'system') {
                  return (
                    <div key={m.id} className={styles.bubbleSystem}>
                      {m.body}
                    </div>
                  );
                }
                const outgoing = m.senderRole === 'instructor';
                return (
                  <div
                    key={m.id}
                    className={`${styles.bubbleWrap} ${outgoing ? styles.bubbleWrapOut : styles.bubbleWrapIn}`}
                  >
                    <div
                      className={`${styles.bubble} ${outgoing ? styles.bubbleOut : styles.bubbleIn} ${
                        m.clientStatus === 'failed' ? styles.bubbleFailed : ''
                      }`}
                    >
                      {m.body}
                    </div>
                    <span className={styles.bubbleTime}>
                      {bubbleTime(m.sentAt)}
                      {m.clientStatus === 'sending' && ' · sending…'}
                      {m.clientStatus === 'failed' && ' · failed'}
                      {m.clientStatus === 'failed' && (
                        <button
                          type="button"
                          onClick={() => handleRetry(m)}
                          style={{
                            marginLeft: 6,
                            padding: 0,
                            border: 'none',
                            background: 'none',
                            color: '#dc2626',
                            font: 'inherit',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                        >
                          Retry
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}
              {orderedMessages.length === 0 && (
                <div className={styles.emptyThread}>
                  No messages yet — say hi.
                </div>
              )}
            </div>

            <div className={styles.composer}>
              <div className={styles.composerTop}>
                <div className={styles.segmented}>
                  <button
                    type="button"
                    className={`${styles.segmentedItem} ${composerTab === 'write' ? styles.segmentedItemActive : ''}`}
                    onClick={() => setComposerTab('write')}
                  >
                    Write
                  </button>
                  <button
                    type="button"
                    className={`${styles.segmentedItem} ${composerTab === 'preview' ? styles.segmentedItemActive : ''}`}
                    onClick={() => setComposerTab('preview')}
                  >
                    Preview
                  </button>
                </div>
                <span className={styles.composerHint}>
                  Markdown supported · ⌘↵ to send
                </span>
                <button
                  type="button"
                  className={styles.sendBtn}
                  onClick={handleSend}
                  disabled={!draft.trim() || sendMessage.isPending}
                >
                  {sendMessage.isPending ? 'Sending…' : 'Send'}
                </button>
              </div>
              {composerTab === 'write' ? (
                <textarea
                  ref={composerRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={handleComposerKey}
                  placeholder={`Reply to ${activeConversation.studentName.split(' ')[0]}…`}
                  className={styles.composerTextarea}
                  aria-label="Message body"
                />
              ) : (
                <div
                  className={styles.composerTextarea}
                  style={{ whiteSpace: 'pre-wrap', overflow: 'auto' }}
                >
                  {draft || 'Nothing to preview yet.'}
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* ---------- Context sidebar ---------- */}
      <aside className={styles.contextPane} aria-label="Student context">
        {!activeConversation ? (
          <div className={styles.contextEmpty}>
            Pick a conversation to see context.
          </div>
        ) : (
          <>
            <span className={styles.contextAvatar} aria-hidden="true">
              {initialsFrom(activeConversation.studentName)}
            </span>
            <div>
              <h3 className={styles.contextName}>{activeConversation.studentName}</h3>
              <p className={styles.contextSubtitle}>
                Autumn 2026 · AI Engineering
              </p>
              {isFlagged && <span className={styles.contextChip}>At risk</span>}
            </div>

            <div className={styles.contextDivider} />

            <div className={styles.contextMetric}>
              <span className={styles.contextLabel}>Mastery</span>
              <span className={styles.contextValue}>
                {studentDetail?.averageScore != null ? `${studentDetail.averageScore}%` : '—'}
              </span>
            </div>
            <div className={styles.contextDivider} />

            <div className={styles.contextMetric}>
              <span className={styles.contextLabel}>Last active</span>
              <span className={styles.contextValue}>
                {studentDetail?.lastActiveAt
                  ? formatDistanceToNowStrict(new Date(studentDetail.lastActiveAt), { addSuffix: true })
                  : '—'}
              </span>
            </div>
            <div className={styles.contextDivider} />

            <div className={styles.contextMetric}>
              <span className={styles.contextLabel}>Open labs</span>
              <span className={styles.contextValue}>
                {studentDetail?.labsInProgress ?? 0}
              </span>
            </div>
            <div className={styles.contextDivider} />

            <div className={styles.contextMetric}>
              <span className={styles.contextLabel}>Enrolled</span>
              <span className={styles.contextValue}>
                {studentDetail?.createdAt
                  ? format(new Date(studentDetail.createdAt), 'MMM d, yyyy')
                  : '—'}
              </span>
            </div>

            {atRiskRecord && atRiskRecord.reasons.length > 0 && (
              <>
                <div className={styles.contextDivider} />
                <div className={styles.flagsSection}>
                  <h4 className={styles.flagsTitle}>Active flags</h4>
                  {atRiskRecord.reasons.map((r) => (
                    <span key={r.type} className={styles.flagChip}>{r.detail}</span>
                  ))}
                </div>
              </>
            )}

            <div className={styles.contextDivider} />

            <div className={styles.quickActions}>
              <h4 className={styles.quickTitle}>Quick actions</h4>
              <button type="button" className={styles.quickPrimary}>Book 1:1</button>
              <button type="button" className={styles.quickGhost}>Add a note</button>
              <Link
                to={`/students/${activeConversation.studentId}`}
                className={styles.quickGhost}
              >
                Open profile
              </Link>
            </div>
          </>
        )}
      </aside>
    </div>
  );
};

export default Messages;
